// src/pages/container_rental/components/RentalCard.tsx
//
// 발행 건 카드.
//
// 구 "freight dossier" 표현(다크 상단 스트라이프 · 의사 컨테이너 코드 ·
// 모서리 브래킷 · 점 그리드 그레인 · hazard 스윕 · 3px 오프셋 그림자 ·
// mono 대문자 코드 라벨)을 전부 걷어내고 홈 카드와 같은 언어로 맞췄다.
//  · 흰 서피스 + #E4E7EA 보더 + radius 12
//  · 호버 = 보더 액센트 + 미세 그림자 (translate 없음)
//  · 금액 · 날짜 · 회차 같은 데이터 표기는 mono tabular 유지
//  · 회차 진행 다이얼은 유지하되 색은 statusVisuals/designTokens 경유

import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import dayjs from "dayjs";
import { designTokens } from "../../../theme.ts";
import {
  ContainerRentalDto,
  RentalScheduleDto,
} from "../../../contracts/container_rental/types.ts";
import { ROUND_VISUALS } from "./statusVisuals.ts";
import StatusChip from "./StatusChip.tsx";

const T = designTokens;

const C = {
  surface: "#FFFFFF",
  field: "#F9FAFB",
  border: "#E4E7EA",
  ink: T.ink,
  sub: "#6B7178",
  caption: "#9AA0A6",
  accent: T.cargo,
  accentTint: "#FDEDE8",
  neutral: "#F2F4F5",
};

interface RentalCardProps {
  rental: ContainerRentalDto;
  onOpen: (id: string) => void;
  onDelete: (rental: ContainerRentalDto) => void;
  onDownloadExcel: (rental: ContainerRentalDto) => void;
}

const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const d = dayjs(iso);
  return d.isValid() ? d.format("YY.MM.DD") : iso;
};

const fmtKrw = (n: number) =>
  Number.isFinite(n) ? new Intl.NumberFormat("ko-KR").format(n) : "—";

function pickFirstSchedule(r: ContainerRentalDto): RentalScheduleDto | undefined {
  if (!r.schedules?.length) return undefined;
  return r.schedules.find((s) => s.round === 1) ?? r.schedules[0];
}

function pickSecondSchedule(r: ContainerRentalDto): RentalScheduleDto | undefined {
  return r.schedules?.find((s) => s.round === 2);
}

export default function RentalCard({
  rental,
  onOpen,
  onDelete,
  onDownloadExcel,
}: RentalCardProps) {
  const dim = rental.status === "closed" || rental.status === "cancelled";
  const first = pickFirstSchedule(rental);
  const second = pickSecondSchedule(rental);

  const totalRounds = rental.totalRounds || rental.schedules?.length || 0;
  const issuedRounds =
    rental.schedules?.filter((s) => s.status === "issued").length ?? 0;

  return (
    <Box
      onClick={() => onOpen(rental.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        // 카드 안의 액션 버튼(상세/엑셀/삭제)에서 올라온 Enter/Space 는
        // 그 버튼의 것이다 — 가로채면 삭제 대신 상세 이동이 일어난다.
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(rental.id);
        }
      }}
      sx={{
        position: "relative",
        cursor: "pointer",
        outline: "none",
        bgcolor: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: "12px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        opacity: dim ? 0.82 : 1,
        transition: "border-color 180ms ease, box-shadow 180ms ease",
        boxShadow: "0 1px 2px rgba(16,24,32,0.06)",
        "&:hover, &:focus-visible": {
          borderColor: C.accent,
          boxShadow: "0 8px 20px rgba(226,69,28,0.10)",
        },
      }}
    >
      {/* 헤더 — 거래처 · 현장 + 상태 */}
      <Box sx={{ px: 2.25, pt: 2.25, pb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.25,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              sx={{
                fontFamily: T.fonts.sans,
                fontWeight: 700,
                fontSize: 17,
                lineHeight: 1.35,
                letterSpacing: "-0.01em",
                color: C.ink,
              }}
            >
              {rental.clientName || "(거래처 미입력)"}
            </Typography>
            <Typography
              noWrap
              sx={{
                mt: 0.4,
                fontFamily: T.fonts.sans,
                fontSize: 13,
                color: C.sub,
              }}
            >
              {rental.siteName || "(현장 미입력)"}
            </Typography>
          </Box>
          <StatusChip status={rental.status} />
        </Box>
      </Box>

      {/* 스펙 */}
      <Box
        sx={{
          mx: 2.25,
          borderTop: `1px solid ${C.border}`,
          borderBottom: `1px solid ${C.border}`,
          py: 1.25,
        }}
      >
        <Stack spacing={0.75}>
          <SpecRow
            label="컨테이너"
            value={
              <>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {rental.containerModel || "—"}
                </Box>
                <Box
                  component="span"
                  sx={{
                    ml: 0.75,
                    color: C.sub,
                    fontFamily: T.fonts.mono,
                    fontSize: 12.5,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  ×{rental.containerCount}
                </Box>
              </>
            }
          />
          <SpecRow
            label="월 공급가"
            mono
            value={
              <>
                ₩{fmtKrw(rental.monthlyRent)}
                <Box
                  component="span"
                  sx={{
                    color: C.caption,
                    fontWeight: 500,
                    ml: 0.5,
                    fontSize: 11,
                    fontFamily: T.fonts.sans,
                  }}
                >
                  /월
                </Box>
              </>
            }
          />
          <SpecRow
            label="1회차 사용기간"
            mono
            value={
              first
                ? `${fmtDate(first.periodStart)} → ${fmtDate(first.periodEnd)}`
                : fmtDate(rental.rentalStartDate)
            }
          />
          <SpecRow
            label="2회차 발행 예정"
            mono
            valueColor={second ? "#8A5C12" : undefined}
            valueWeight={second ? 700 : 600}
            value={second ? fmtDate(second.issueDate) : "—"}
          />
        </Stack>
      </Box>

      {/* 회차 진행 다이얼 */}
      {totalRounds > 0 && (
        <Box sx={{ px: 2.25, mt: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.6,
            }}
          >
            <Box
              sx={{
                fontFamily: T.fonts.sans,
                fontSize: 12,
                fontWeight: 500,
                color: C.sub,
              }}
            >
              회차 진행
            </Box>
            <Box
              sx={{
                fontFamily: T.fonts.mono,
                fontSize: 12,
                fontWeight: 700,
                color: C.ink,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {issuedRounds}
              <Box component="span" sx={{ color: C.caption, mx: 0.4, fontWeight: 500 }}>
                /
              </Box>
              {totalRounds}
            </Box>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${Math.max(totalRounds, 1)}, 1fr)`,
              gap: "2px",
              bgcolor: C.neutral,
              borderRadius: "6px",
              p: "2px",
            }}
          >
            {Array.from({ length: totalRounds }).map((_, idx) => {
              const sch = rental.schedules?.find((s) => s.round === idx + 1);
              const status = sch?.status;
              const fill =
                status === "issued"
                  ? ROUND_VISUALS.issued.accent
                  : status === "skipped"
                  ? ROUND_VISUALS.skipped.accent
                  : status === "cancelled"
                  ? ROUND_VISUALS.cancelled.accent
                  : "transparent";
              return (
                <Box
                  key={idx}
                  sx={{
                    height: 8,
                    borderRadius: "3px",
                    bgcolor: fill,
                    opacity:
                      status === "skipped" || status === "cancelled" ? 0.55 : 1,
                  }}
                />
              );
            })}
          </Box>
        </Box>
      )}

      {/* 1회차 합계 */}
      <Box
        sx={{
          mx: 2.25,
          mt: 1.75,
          bgcolor: C.field,
          border: `1px solid ${C.border}`,
          borderRadius: "8px",
          px: 1.5,
          py: 1.1,
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box
          sx={{
            fontFamily: T.fonts.sans,
            fontSize: 12.5,
            fontWeight: 500,
            color: C.sub,
          }}
        >
          1회차 합계
        </Box>
        <Box
          sx={{
            fontFamily: T.fonts.mono,
            fontWeight: 700,
            fontSize: 17,
            color: C.ink,
            letterSpacing: "-0.01em",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {first ? `₩${fmtKrw(first.total)}` : "—"}
        </Box>
      </Box>

      {/* 액션 */}
      <Box
        sx={{
          mx: 2.25,
          mb: 2,
          mt: "auto",
          pt: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box
          sx={{
            fontFamily: T.fonts.sans,
            fontSize: 12,
            color: C.caption,
          }}
        >
          수정{" "}
          <Box
            component="span"
            sx={{
              fontFamily: T.fonts.mono,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {fmtDate(rental.modifiedTimestamp)}
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <ActionIconBtn
            label="상세보기"
            onClick={(e) => { e.stopPropagation(); onOpen(rental.id); }}
          >
            <OpenInNewIcon sx={{ fontSize: 16 }} />
          </ActionIconBtn>
          <ActionIconBtn
            label="엑셀 다운로드"
            onClick={(e) => { e.stopPropagation(); onDownloadExcel(rental); }}
          >
            <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
          </ActionIconBtn>
          <ActionIconBtn
            label="삭제"
            onClick={(e) => { e.stopPropagation(); onDelete(rental); }}
            danger
          >
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </ActionIconBtn>
        </Box>
      </Box>
    </Box>
  );
}

// ---------- Subcomponents ----------

function SpecRow({
  label,
  value,
  mono,
  valueColor,
  valueWeight,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  valueColor?: string;
  valueWeight?: number;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
      <Box
        sx={{
          fontFamily: T.fonts.sans,
          fontSize: 12.5,
          fontWeight: 500,
          color: C.sub,
          flexShrink: 0,
        }}
      >
        {label}
      </Box>
      <Box
        sx={{
          fontFamily: mono ? T.fonts.mono : T.fonts.sans,
          fontSize: mono ? 12.5 : 13,
          fontWeight: valueWeight ?? 600,
          color: valueColor ?? C.ink,
          fontVariantNumeric: mono ? "tabular-nums" : undefined,
          textAlign: "right",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Box>
    </Box>
  );
}

function ActionIconBtn({
  label,
  onClick,
  children,
  danger,
}: {
  label: string;
  onClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
  danger?: boolean;
}) {
  return (
    <Tooltip title={label}>
      <IconButton
        size="small"
        onClick={onClick}
        aria-label={label}
        sx={{
          borderRadius: "8px",
          border: `1px solid ${C.border}`,
          width: 30,
          height: 30,
          bgcolor: C.surface,
          color: C.sub,
          transition: "background-color 150ms ease, color 150ms ease, border-color 150ms ease",
          "&:hover": danger
            ? { bgcolor: "#FBEDED", borderColor: "#F0D6D6", color: T.stop }
            : { bgcolor: C.accentTint, borderColor: C.accentTint, color: C.accent },
        }}
      >
        {children}
      </IconButton>
    </Tooltip>
  );
}
